const DEFAULT_OPTION = {
  enabled: false,
  ask_username: false,
  ask_for_password: false,
  ask_for_password_end: false,
  skip_billing_info: false,
  skip_payment_info: false,
  skip_terms_or_service: false,
  skip_phone: false,
};

const sameId = (a, b) => String(a) === String(b);

function isMainPricePoint(point) {
  return point.is_pinned === undefined
    || point.is_pinned === null
    || point.is_pinned === true
    || Number(point.is_pinned) === 1;
}

/**
 * Sort price points by their position in a checkout link's pricing_options[].
 * Main-list price points always stay together before hidden price points so the
 * checkout editor's hidden divider cannot hide a later visible offer.
 * Price points absent from pricing_options keep their relative order and follow.
 */
export function orderPricePointsByOptions(pricePoints, pricingOptions) {
  const points = Array.isArray(pricePoints) ? pricePoints.slice() : [];
  const options = Array.isArray(pricingOptions) ? pricingOptions : [];

  const position = new Map();
  options.forEach((option, index) => {
    if (option && option.id != null && !position.has(String(option.id))) {
      position.set(String(option.id), index);
    }
  });

  return points
    .map((point, index) => ({
      point,
      index,
      optionIndex: position.get(String(point.id)),
    }))
    .sort((a, b) => {
      const visibilityOrder = Number(isMainPricePoint(b.point)) - Number(isMainPricePoint(a.point));
      if (visibilityOrder) return visibilityOrder;

      const aHasOption = a.optionIndex !== undefined;
      const bHasOption = b.optionIndex !== undefined;
      if (aHasOption !== bHasOption) return aHasOption ? -1 : 1;
      if (aHasOption && a.optionIndex !== b.optionIndex) return a.optionIndex - b.optionIndex;
      return a.index - b.index;
    })
    .map(entry => entry.point);
}

/**
 * Rebuild pricing_options[] in the displayed order of price points,
 * preserving every existing per-price-point field by id.
 *
 * Existing options whose price point is not in `orderedPricePoints` (e.g. price
 * points filtered out of the current checkout type) are kept, appended after the
 * ordered entries with `enabled: false`, so their settings are never lost.
 * The result is always a dense array.
 */
export function buildPricingOptions(orderedPricePoints, existingOptions) {
  const points = Array.isArray(orderedPricePoints) ? orderedPricePoints : [];
  const existing = Array.isArray(existingOptions) ? existingOptions : [];

  const ordered = points.map((point) => {
    const current = existing.find(option => option && sameId(option.id, point.id));
    return Object.assign({}, DEFAULT_OPTION, current || {}, {
      id: point.id,
      enabled: Boolean(point.enabled),
    });
  });

  const retained = existing
    .filter(option => option && !points.some(point => sameId(option.id, point.id)))
    .map(option => Object.assign({}, option, { enabled: false }));

  return ordered.concat(retained);
}
